import cn from 'clsx'
import Dropzone from 'react-dropzone'
import {Collapse} from 'antd'
import useStore from 'base/store'
import styles from './DocumentsList.module.scss'
import {useGetProjectConditions} from 'modules/project'
import {Box, Text, DropdownIcon} from 'common/components'
import {useOpen} from 'common/hooks'
import {useUploadFiles} from 'common/api'
import {formatDate} from 'common/helpers'
import {
  Document,
  useAddDocuments,
  IDocument,
  IDocumentsTab,
  TDocumentsModel,
} from '..'

interface DocumentsListProps {
  projectUuid: string
  modelUuid?: string
  modelType?: TDocumentsModel
  collection?: string
  columns?: number
  search?: string
  title?: string
  description?: string
  documents: IDocument[]
  drawdownsTabs?: IDocumentsTab[]
  amlTabs?: IDocumentsTab[]
}

interface TitleProps {
  title: string
  description: string
}

export default function DocumentsList({
  projectUuid,
  modelUuid,
  modelType,
  collection,
  columns,
  search,
  title,
  description,
  documents,
  drawdownsTabs,
  amlTabs,
}: DocumentsListProps) {
  const role = useStore((state) => state.role)
  const {mutate: addDocuments} = useAddDocuments({
    projectUuid,
    modelUuid,
    modelType,
    collection,
  })
  const {mutate: uploadFiles} = useUploadFiles({
    key: collection,
    attachFiles: (filesUuids) => {
      addDocuments(filesUuids)
    },
  })
  const [isDrag, handleDragStart, handleDragEnd] = useOpen()
  const isConditions = collection === 'documents'
  const {data: conditions} = useGetProjectConditions(projectUuid, isConditions)

  let currentDocuments = documents.filter((document) => !document.archived)
  let archivedDocuments = documents.filter((document) => document.archived)

  return (
    <Dropzone
      noClick
      noKeyboard
      disabled={!collection || isConditions || !!role?.user}
      onDrop={(files: any) => uploadFiles(files)}
      onDragEnter={handleDragStart}
      onDragLeave={handleDragEnd}
      onDropAccepted={handleDragEnd}
    >
      {({getRootProps, getInputProps}) => (
        <div
          {...getRootProps()}
          className={cn(
            styles.content,
            columns && styles.content_columns,
            isDrag && styles.content_dragAccept
          )}
        >
          <input {...getInputProps()} />
          {title && <Title title={title} description={description} />}
          <div>
            {isConditions ? (
              <ul>
                {conditions
                  .filter((condition: any) => condition.documents.length)
                  .map((condition: any) => (
                    <li key={condition.uuid} className={styles.condition}>
                      <Title
                        title={formatDate(condition.date, 'fifth')}
                        description={condition.description}
                      />
                      <List
                        projectUuid={projectUuid}
                        columns={columns}
                        search={search}
                        documents={documents.filter(
                          (document) => document.model.uuid === condition.uuid
                        )}
                        drawdownsTabs={drawdownsTabs}
                        amlTabs={amlTabs}
                      />
                    </li>
                  ))}
              </ul>
            ) : (
              <>
                <List
                  projectUuid={projectUuid}
                  columns={columns}
                  search={search}
                  documents={currentDocuments}
                  drawdownsTabs={drawdownsTabs}
                  amlTabs={amlTabs}
                />
                {Boolean(archivedDocuments.length) && (
                  <Collapse
                    bordered={false}
                    className={styles.collapse}
                    expandIconPosition="end"
                    expandIcon={CollapseIcon}
                  >
                    <CollapsePanel
                      key="archived"
                      header={
                        <Text weight="demiBold">
                          Archived documents
                          <Text as="span" color="atmo" ml={6}>
                            {archivedDocuments.length}
                          </Text>
                        </Text>
                      }
                    >
                      <List
                        projectUuid={projectUuid}
                        search={search}
                        columns={columns}
                        documents={archivedDocuments}
                      />
                    </CollapsePanel>
                  </Collapse>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </Dropzone>
  )
}

const List = ({
  projectUuid,
  columns,
  search,
  documents,
  drawdownsTabs,
  amlTabs,
}: DocumentsListProps) => {
  if (!documents.length) return null

  const listStyle = columns
    ? {gridTemplateColumns: `repeat(${columns}, 1fr)`}
    : undefined

  return (
    <ul className={styles.list} style={listStyle}>
      {documents.map((document) => (
        <li key={document.uuid}>
          <Document
            projectUuid={projectUuid}
            search={search}
            document={document}
            drawdownsTabs={drawdownsTabs}
            amlTabs={amlTabs}
          />
        </li>
      ))}
    </ul>
  )
}

const CollapseIcon = () => (
  <div>
    <DropdownIcon width={9} height="100%" />
  </div>
)

const Title = ({title, description}: TitleProps) => {
  return (
    <Box mb={12}>
      <Text as="h4" weight="demiBold">
        {title}
      </Text>
      {description && (
        <Text color="grey" size="2xs" mt={4}>
          {description}
        </Text>
      )}
    </Box>
  )
}

const {Panel: CollapsePanel} = Collapse
